"use client";

import { useRouter } from "next/navigation";
import { useState, useEffect, useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/themes/ui/button";
import { useUser } from "@/providers/userContext";
import ExtraNav from "@/components/themes/ExtraNav";
import { handleGetCartInfoStorageItems } from "@/utilities/cartManager";
import { SK, AK } from "@/constants";
import { AxiosPost } from "@/utilities/axioscall";
import { checkerString, checkerStringLen } from "@/utilities/checker";
import CartItemsSection from "@/components/cart/CartItemsSection";
import PriceSummarySection from "@/components/cart/PriceSummarySection";
import Image from "next/image";
import Link from "next/link";
import LoaderGif from "@/public/ecomsaas-assets/loader/loader.gif";
import SvgIcons from "@/components/themes/SvgIcons";

export const revalidate = 60;
export const runtime = "edge";

export default function CartView() {
  const [pageinit, setPageInit] = useState(false);
  const { user, login, cartinfoData } = useUser();
  const [cartItems, setCartItems] = useState(cartinfoData);
  const setCartInfoData = (data: any) => { };
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(true);
 
  useEffect(() => {
    const initializeCartData = async () => {
      setIsLoading(true);
      try {
        handleGetCartInfoStorageItems(setCartInfoData);

        const storedCart = localStorage.getItem(SK.CARTINFODATA);
        if (storedCart) {
          const parsedCart = JSON.parse(storedCart);
          if (
            Array.isArray(parsedCart.products) &&
            parsedCart.products.length > 0
          ) {
            setCartItems(parsedCart.products);
            setIsLoading(false);
            return;
          }
        }

      } catch (error) {
        console.error("Error initializing cart:", error);
      } finally {
        setIsLoading(false);
      }
    };
    if (!pageinit) {
      initializeCartData();
      setPageInit(true);
    }
  }, [setCartItems, setCartInfoData, user, setIsLoading]);

 

  if (isLoading) {
    return (
      <>
        <section className="sticky top-0 z-50 md:hidden">
          <div className="bg-white flex justify-between items-center px-4 py-3 h-12 shadow-sm">
            <div className="cursor-pointer" onClick={() => router.push("/")}>
              <SvgIcons.CommonBackIcon />
            </div>
            <h1 className="font-semibold text-lg">My Cart</h1>
            <div className="invisible">
              <SvgIcons.CommonBackIcon />
            </div>
          </div>
        </section>

        <section className="sticky top-0 z-50 hidden md:block">
          <div className="bg-white flex justify-center items-center px-4 py-3 h-12 shadow-sm">
            <Link href="/">
              <img
                className="w-40"
                src={
                  process.env.NEXT_PUBLIC_STORE_CODE
                    ? `https://assetsvilva.blr1.cdn.digitaloceanspaces.com/ecomsaas/redisapi/${process.env.NEXT_PUBLIC_STORE_CODE}/logos/logowebp.webp`
                    : "https://vilva-backup.blr1.digitaloceanspaces.com/vilvabusiness/common%20files/empty-logowebp.webp"
                }
                onError={(e) => {
                  e.currentTarget.src =
                    "https://vilva-backup.blr1.digitaloceanspaces.com/vilvabusiness/common%20files/empty-logowebp.webp";
                }}
                alt="Store Logo"
              />
            </Link>
          </div>
        </section>
        <div className="flex justify-center items-center min-h-[70vh]">
          <Image src={LoaderGif} alt="Loader Gif" />
        </div>

        <div className="justify-center mt-4">
          <div>
            <p className="flex items-start p-[16px] md:items-center justify-center gap-2 text-sm">
              <SvgIcons.SecureBadge />
              Safe and Secure Payments. 100% Authentic products.
            </p>
            <img
              src="https://assetsvilva.blr1.cdn.digitaloceanspaces.com/icons/secure-payments-image_11zon.webp"
              className="w-full md:w-[500px] mx-auto"
              alt="cart payment image"
            />
          </div>
        </div>
      </>
    );
  }



  return (
    <div>
      <ExtraNav />
      <div className="max-w-7xl mx-auto">

        {cartinfoData && typeof cartinfoData.cartcount === 'number' && cartinfoData.cartcount > 0 ? (
          <>
            <div className="mt-4 gap-4 md:mb-4 md:block hidden">
              <div className="flex items-center gap-4 md:mb-4">
                <Button variant="ghost" size="icon" onClick={() => router.back()}>
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="md:text-[20px] text-[16px] font-bold">
                  My Cart ({cartinfoData?.cartcount || 0})
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap">
              <CartItemsSection
                isLoading={isLoading}
                userData={user}
                cartItems={cartinfoData?.products || []}
                itemCount={cartinfoData?.cartcount || 0}
              />
              <PriceSummarySection setCartInfoData={setCartInfoData} />
            </div>
          </>
        ) : (
          <>
            <div className="max-w-7xl mx-auto h-[75vh] flex justify-center items-center">
              <div>
                <div className="bg-white p-4 rounded-lg text-center">
                  <img
                    src="/ecomsaas-assets/loader/empty-cart.webp"
                    alt="Empty Cart"
                    className="mx-auto w-full sm:w-[300px] md:w-[500px]"
                  />
                  <h3 className="text-xl font-bold mt-4">Your Cart is Empty</h3>
                  <p className="text-gray-600 mt-2 text-center">
                    Looks like you have no items in your shopping cart. <br />
                    Click{" "}
                    <Link href="/products/All-Products" className="text-blue-600 underline">
                      here
                    </Link>{" "}
                    to continue shopping.
                  </p>
                </div>
                <div className="justify-center mt-9 px-4">
                  <div>
                    <p className="flex items-start p-[16px] md:items-center justify-center gap-2 text-sm">
                      <SvgIcons.SecureBadge />
                      Safe and Secure Payments. 100% Authentic products.
                    </p>
                    <img
                      src="https://assetsvilva.blr1.cdn.digitaloceanspaces.com/icons/secure-payments-image_11zon.webp"
                      className="w-full sm:w-[500px] mx-auto"
                      alt="cart payment image"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}